import { calculate } from '../engine/calculate';
import type { CalculatorInput, FeeProfile, MarketplaceCode } from '../engine/types';
import type { Store } from '../state/store';
import { findElementById, numberValue, setHidden, type DomRefs } from './dom';
import { formatMoney } from './format';
import { render } from './render';

const KG_TO_LB = 2.2046226218;
const LB_TO_KG = 1 / KG_TO_LB;
const CM_TO_IN = 0.3937007874;
const IN_TO_CM = 1 / CM_TO_IN;

const INVALID_INPUT_CLASSES = ['border-red-400', 'focus:border-red-400', 'focus:ring-red-100', 'bg-red-50'] as const;

type MeasurementInputs = Pick<DomRefs, 'weight' | 'length' | 'width' | 'height'>;

interface NumericValidationRule {
  node: HTMLInputElement;
  label: string;
  required?: boolean;
  nonNegative?: boolean;
  strictlyPositive?: boolean;
  skip?: () => boolean;
}

function isElementHidden(element: HTMLElement): boolean {
  return element.hasAttribute('hidden') || element.style.display === 'none' || element.classList.contains('hidden');
}

export function isMetricMarketplace(market: MarketplaceCode): boolean {
  return market === 'CA' || market === 'UK' || market === 'DE';
}

export function formatInputNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function setWarning(dom: DomRefs, message: string): void {
  dom.calcWarning.textContent = message;
  setHidden(dom.calcWarning, message.length === 0);
}

function setMeasurementLabels(dom: DomRefs, market: MarketplaceCode): void {
  const metric = isMetricMarketplace(market);
  dom.weightUnitLabel.textContent = metric ? 'kg' : 'lb';
  dom.dimensionUnitLabel.textContent = metric ? 'centimeters' : 'inches';
}

export function convertDisplayedMeasurements(
  inputs: MeasurementInputs,
  fromMarket: MarketplaceCode,
  toMarket: MarketplaceCode
): void {
  const fromMetric = isMetricMarketplace(fromMarket);
  const toMetric = isMetricMarketplace(toMarket);
  if (fromMetric === toMetric) return;

  const convertField = (node: HTMLInputElement, converter: (value: number) => number) => {
    const raw = Number(node.value);
    if (!Number.isFinite(raw)) return;
    node.value = formatInputNumber(converter(raw));
  };

  if (fromMetric && !toMetric) {
    convertField(inputs.weight, (value) => value * KG_TO_LB);
    convertField(inputs.length, (value) => value * CM_TO_IN);
    convertField(inputs.width, (value) => value * CM_TO_IN);
    convertField(inputs.height, (value) => value * CM_TO_IN);
    return;
  }

  convertField(inputs.weight, (value) => value * LB_TO_KG);
  convertField(inputs.length, (value) => value * IN_TO_CM);
  convertField(inputs.width, (value) => value * IN_TO_CM);
  convertField(inputs.height, (value) => value * IN_TO_CM);
}

function applyValidationState(node: HTMLInputElement, message: string): void {
  node.setCustomValidity(message);
  node.setAttribute('aria-invalid', message ? 'true' : 'false');
  node.title = message;
  for (const className of INVALID_INPUT_CLASSES) {
    node.classList.toggle(className, Boolean(message));
  }
}

function buildValidationRules(dom: DomRefs): NumericValidationRule[] {
  return [
    { node: dom.price, label: 'Selling price', required: true, strictlyPositive: true },
    { node: dom.cogs, label: 'Cost of goods sold', required: true, nonNegative: true },
    { node: dom.weight, label: 'Weight', required: true, strictlyPositive: true },
    { node: dom.length, label: 'Product length', required: true, strictlyPositive: true },
    { node: dom.width, label: 'Product width', required: true, strictlyPositive: true },
    { node: dom.height, label: 'Product height', required: true, strictlyPositive: true },
    { node: dom.inbound, label: 'Inbound freight', nonNegative: true },
    { node: dom.duties, label: 'Duties and tariffs', nonNegative: true },
    { node: dom.prep, label: 'Prep and pack cost', nonNegative: true },
    {
      node: dom.inboundPlacementManual,
      label: 'Inbound placement fee',
      nonNegative: true,
      skip: () => dom.inboundPlacementManual.disabled
    }
  ];
}

function getValidationError(rule: NumericValidationRule): string {
  if (rule.skip?.()) return '';

  const raw = rule.node.value.trim();
  if (rule.required && raw.length === 0) {
    return `${rule.label} is required.`;
  }

  if (raw.length === 0) return '';

  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return `Enter a valid number for ${rule.label.toLowerCase()}.`;
  }

  if (rule.strictlyPositive && value <= 0) {
    return `${rule.label} must be greater than 0.`;
  }

  if (rule.nonNegative && value < 0) {
    return `${rule.label} cannot be negative.`;
  }

  return '';
}

function validateInputNode(dom: DomRefs, node: HTMLInputElement): boolean {
  const rule = buildValidationRules(dom).find((item) => item.node === node);
  if (!rule) return true;

  const error = getValidationError(rule);
  applyValidationState(node, error);
  return !error;
}

function validateAllInputs(dom: DomRefs): { ok: boolean; firstInvalid?: HTMLInputElement } {
  const rules = buildValidationRules(dom);
  let firstInvalid: HTMLInputElement | undefined;

  for (const rule of rules) {
    const error = getValidationError(rule);
    applyValidationState(rule.node, error);
    if (error && !firstInvalid) {
      firstInvalid = rule.node;
    }
  }

  return { ok: !firstInvalid, firstInvalid };
}

function normalizeNumericInput(node: HTMLInputElement): void {
  const raw = node.value.trim();
  if (!raw) return;

  const value = Number(raw);
  if (!Number.isFinite(value)) return;

  node.value = formatInputNumber(value);
}

function bindInputValidationAndFormatting(dom: DomRefs): void {
  const numericInputs = [
    dom.price,
    dom.cogs,
    dom.weight,
    dom.length,
    dom.width,
    dom.height,
    dom.inbound,
    dom.duties,
    dom.prep,
    dom.inboundPlacementManual
  ];

  for (const input of numericInputs) {
    input.addEventListener('input', () => {
      validateInputNode(dom, input);
    });

    input.addEventListener('blur', () => {
      normalizeNumericInput(input);
      validateInputNode(dom, input);
    });
  }
}

function populateCategories(dom: DomRefs, profile: FeeProfile): void {
  const current = dom.category.value;
  dom.category.innerHTML = '';
  const sorted = [...profile.referralFees].sort((a, b) => a.category.localeCompare(b.category));
  for (const item of sorted) {
    const option = dom.category.ownerDocument.createElement('option');
    option.value = item.category;
    option.textContent = item.category;
    dom.category.appendChild(option);
  }

  if (!sorted.length) return;
  const hasCurrent = sorted.some((item) => item.category === current);
  if (hasCurrent) {
    dom.category.value = current;
  } else {
    const home = sorted.find((item) => item.category === 'Home and Kitchen');
    dom.category.value = home ? home.category : sorted[0]?.category ?? '';
  }
}

function parseInput(dom: DomRefs, market: MarketplaceCode): CalculatorInput {
  const metric = isMetricMarketplace(market);
  const weightRaw = numberValue(dom.weight);
  const lengthRaw = numberValue(dom.length);
  const widthRaw = numberValue(dom.width);
  const heightRaw = numberValue(dom.height);

  return {
    category: dom.category.value,
    price: numberValue(dom.price),
    cogs: numberValue(dom.cogs),
    weight: metric ? weightRaw * KG_TO_LB : weightRaw,
    length: metric ? lengthRaw * CM_TO_IN : lengthRaw,
    width: metric ? widthRaw * CM_TO_IN : widthRaw,
    height: metric ? heightRaw * CM_TO_IN : heightRaw,
    inbound: numberValue(dom.inbound),
    duties: numberValue(dom.duties),
    prep: numberValue(dom.prep),
    inboundPlacementMode: dom.inboundPlacementMode.value as CalculatorInput['inboundPlacementMode'],
    inboundPlacementManual: numberValue(dom.inboundPlacementManual)
  };
}

function refreshInboundPlacementField(dom: DomRefs): void {
  const useManual = dom.inboundPlacementMode.value === 'minimal' || dom.inboundPlacementMode.value === 'manual';
  dom.inboundPlacementManual.disabled = !useManual;
  dom.inboundPlacementManualWrap.classList.toggle('opacity-60', !useManual);
  if (!useManual) {
    dom.inboundPlacementManual.value = '0';
    applyValidationState(dom.inboundPlacementManual, '');
  }
}

export function computeSliderRange(basePrice: number): { min: number; max: number } {
  if (basePrice > 40) {
    return { min: basePrice * 0.5, max: basePrice * 1.5 };
  }
  return { min: Math.max(0, basePrice - 5), max: basePrice + 5 };
}

function updateSliderProgress(slider: HTMLInputElement): void {
  const min = Number(slider.min);
  const max = Number(slider.max);
  const value = Number(slider.value);

  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(value) || max <= min) {
    slider.style.setProperty('--apc-slider-progress', '0%');
    return;
  }

  const clamped = Math.min(Math.max(value, min), max);
  const percent = ((clamped - min) / (max - min)) * 100;
  slider.style.setProperty('--apc-slider-progress', `${percent}%`);
}

function configureSlider(dom: DomRefs, store: Store, basePrice: number): void {
  const profile = store.getState().profile;
  const { min, max } = computeSliderRange(basePrice);

  dom.priceSlider.min = min.toFixed(2);
  dom.priceSlider.max = max.toFixed(2);
  dom.priceSlider.step = '0.01';
  dom.priceSlider.value = basePrice.toFixed(2);
  updateSliderProgress(dom.priceSlider);
  dom.sliderValue.textContent = formatMoney(basePrice, profile.locale, profile.currency);
  setHidden(dom.sliderContainer, false);

  dom.priceSlider.oninput = () => {
    const newPrice = Number(dom.priceSlider.value);
    if (!Number.isFinite(newPrice)) return;
    updateSliderProgress(dom.priceSlider);
    dom.price.value = newPrice.toFixed(2);
    const ok = calculateAndRender(store, dom, newPrice);
    if (ok) {
      const current = store.getState().profile;
      dom.sliderValue.textContent = formatMoney(newPrice, current.locale, current.currency);
    }
  };
}

function calculateAndRender(store: Store, dom: DomRefs, priceOverride?: number): boolean {
  const validation = validateAllInputs(dom);
  if (!validation.ok) {
    setWarning(dom, 'Fix highlighted inputs before calculating.');
    setHidden(dom.results, true);
    validation.firstInvalid?.focus();
    validation.firstInvalid?.reportValidity();
    return false;
  }

  const state = store.getState();
  const input = parseInput(dom, state.market);

  if (Number.isFinite(priceOverride)) {
    input.price = priceOverride as number;
  }

  if (!input.category || input.price <= 0) {
    setWarning(dom, 'Enter required values before calculating.');
    return false;
  }

  const result = calculate(input, state.profile);
  store.setState({ ...state, input, result });
  setWarning(dom, '');
  render(store.getState(), dom.root);
  return true;
}

export function attachEvents(store: Store, dom: DomRefs, profiles: Map<MarketplaceCode, FeeProfile>): void {
  let currentMeasurementMarket = store.getState().market;

  bindInputValidationAndFormatting(dom);

  setHidden(dom.advancedFields, isElementHidden(dom.advancedFields));
  setHidden(dom.sliderContainer, isElementHidden(dom.sliderContainer));
  setHidden(dom.results, isElementHidden(dom.results));
  setWarning(dom, '');

  dom.advancedToggle.addEventListener('click', () => {
    const willHide = !isElementHidden(dom.advancedFields);
    setHidden(dom.advancedFields, willHide);
    dom.advancedIcon.innerHTML = willHide ? '&#9662;' : '&#9652;';
  });

  dom.inboundPlacementMode.addEventListener('change', () => {
    refreshInboundPlacementField(dom);
    validateInputNode(dom, dom.inboundPlacementManual);
  });
  refreshInboundPlacementField(dom);

  dom.marketplace.addEventListener('change', () => {
    const market = dom.marketplace.value as MarketplaceCode;
    const profile = profiles.get(market);
    if (!profile) {
      setWarning(dom, `Fee profile missing for ${market}.`);
      setHidden(dom.results, true);
      return;
    }

    convertDisplayedMeasurements(dom, currentMeasurementMarket, market);
    currentMeasurementMarket = market;

    const hadResults = store.getState().result !== null;
    const next = { ...store.getState(), market, profile, result: hadResults ? store.getState().result : null };
    store.setState(next);

    populateCategories(dom, profile);
    setMeasurementLabels(dom, market);

    if (hadResults) {
      const ok = calculateAndRender(store, dom);
      if (ok) {
        configureSlider(dom, store, numberValue(dom.price));
      }
    } else {
      render(store.getState(), dom.root);
    }

    setWarning(dom, '');
  });

  dom.form.addEventListener('submit', (event) => {
    event.preventDefault();
    const ok = calculateAndRender(store, dom);
    if (ok) {
      configureSlider(dom, store, numberValue(dom.price));
    }
  });

  const initial = store.getState();
  populateCategories(dom, initial.profile);
  setMeasurementLabels(dom, initial.market);

  // Ensure host wrapper retains authoritative hidden behavior in hostile theme CSS environments.
  const host = findElementById<HTMLElement>(dom.root, 'apc-v3-host');
  if (host) {
    host.setAttribute('data-apc-mounted', '1');
  }
}

